import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { requestMobile } from '@/lib/request'
import { toast } from 'sonner'

interface BindUsdtDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountInfo: {
    plat: string
    remarks_name: string
  }
  onSuccess?: () => void
}

export function BindUsdtDialog({ open, onOpenChange, accountInfo, onSuccess }: BindUsdtDialogProps) {
  const [loading, setLoading] = useState(false)
  const [usdtId, setUsdtId] = useState<string>('')
  const [formData, setFormData] = useState({
    real_name: '',
    bank_account: '' // USDT地址
  })

  // 当对话框打开时检查USDT支持
  useEffect(() => {
    if (open) {
      checkUsdtSupport()
    } else {
      resetDialog()
    }
  }, [open])

  const checkUsdtSupport = async () => {
    try {
      const resp = await requestMobile('/common/apis/v1/bind-card-list', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name
      })

      if (resp && resp.data && !resp.code) {
        const specialBankInfoMap = resp.data.specialBankInfoMap || {}
        let foundUsdtId = ''

        Object.keys(specialBankInfoMap).forEach(key => {
          if (key.indexOf('USDT') > -1) {
            foundUsdtId = specialBankInfoMap[key]
          }
        })

        if (foundUsdtId) {
          setUsdtId(foundUsdtId)
        } else {
          toast.error('不支持USDT绑定')
          onOpenChange(false)
        }
      }
    } catch (error) {
      console.error('Failed to check USDT support:', error)
      toast.error('检查USDT支持失败')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.real_name || !formData.bank_account) {
      toast.error('请填写完整信息')
      return
    }

    if (!usdtId) {
      toast.error('USDT ID 不存在')
      return
    }

    setLoading(true)
    try {
      const name = 'USDT钱包'
      const param = {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name,
        real_name: formData.real_name,
        bank_account: formData.bank_account,
        bank_address: name,
        bank_name: name,
        bank_code: name,
        bank_id: usdtId,
        type: 1
      }

      const resp = await requestMobile('/common/apis/v1/bind-card', param)

      if (resp && resp.code === 0) {
        toast.success('绑U成功')
        onOpenChange(false)
        resetDialog()
        onSuccess?.()
      }
    } catch (error) {
      console.error('Failed to bind USDT:', error)
      toast.error('绑U失败')
    } finally {
      setLoading(false)
    }
  }

  const resetDialog = () => {
    setFormData({
      real_name: '',
      bank_account: ''
    })
    setUsdtId('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>绑定USDT钱包 {usdtId && `(${usdtId})`}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 姓名 */}
          <div className="space-y-2">
            <Label htmlFor="real_name">姓名</Label>
            <Input
              id="real_name"
              placeholder="请输入姓名"
              value={formData.real_name}
              onChange={(e) => setFormData({ ...formData, real_name: e.target.value })}
              required
            />
          </div>

          {/* USDT地址 */}
          <div className="space-y-2">
            <Label htmlFor="bank_account">USDT地址</Label>
            <Input
              id="bank_account"
              placeholder="请输入USDT地址"
              value={formData.bank_account}
              onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
              required
            />
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              取消
            </Button>
            <Button type="submit" disabled={loading || !usdtId} className="flex-1">
              {loading ? '提交中...' : '提交'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
